<script setup lang="ts">
import { ref } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

import {
  useBrowserStore,
  usePaxStore,
} from '@apps/main/new-desktop/core/stores';
import { useMapStore } from '@apps/main/new-desktop/core/stores/mapStore/mapStore';

import { isFeatureToggleActive } from '@libs/feature-toggle';

import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
  useTripSummaryStore,
} from '../../stores';
import TripOverviewMap from '../TripOverviewMap.vue';

import TripSummaryColumnMobileLoader from './TripSummaryColumnMobileLoader.vue';
import TripSummaryColumnFooter from './TripSummaryColumnFooter.vue';
import TripPriceSummary from './TripPriceSummary.vue';

const tripSummaryStore = useTripSummaryStore();
const paxStore = usePaxStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const tripOverviewPageStore = useTripOverviewPageStore();
const browserStore = useBrowserStore();
const mapStore = useMapStore();

const tripSummaryRef = ref(null);

useIntersectionObserver(
  tripSummaryRef,
  (entries: IntersectionObserverEntry[]) => {
    if (entries[0]) {
      tripSummaryStore.isTripSummaryVisible = entries[0].isIntersecting;
    }
  }
);
</script>

<template>
  <div class="trip-summary-column">
    <template v-if="browserStore.isLargerThan1199px">
      <TripOverviewMap
        v-if="!mapStore.shouldShowFullScreenMap"
        class="map"
        :should-have-border-radius="false"
        @zoom="mapStore.openFullScreenDynamicTripMap()"
      />

      <TripSummaryColumnFooter
        :paxes="paxStore.getPaxesButtonSimpleText"
        :travel-dates="tripSummaryStore.travelDateRange"
        :total-price="tripSummaryStore.tripTotalPrice"
        :gross-price="tripSummaryStore.tripGrossPrice"
        :total-addons-price="tripSummaryStore.tripTotalAddonsPrice"
        :currency="tripSummaryStore.tripSummaryCurrency"
        :should-show-share-button="true"
        class="footer"
        @total-price-clicked="tripOverviewPanelsStore.openTripPriceSummaryPanel"
      />
    </template>

    <template v-else>
      <template
        v-if="
          tripOverviewPageStore.anyItineraryLoaded &&
          !isFeatureToggleActive('loader')
        "
      >
        <div ref="tripSummaryRef">
          <TripPriceSummary
            class="trip-summary-price-summary"
            :price-segments="tripSummaryStore.tripPriceSegments"
          />

          <TripSummaryColumnFooter
            :paxes="paxStore.getPaxesButtonSimpleText"
            :travel-dates="tripSummaryStore.travelDateRange"
            :currency="tripSummaryStore.tripSummaryCurrency"
            :total-price="tripSummaryStore.tripTotalPrice"
            :gross-price="tripSummaryStore.tripGrossPrice"
            :total-addons-price="tripSummaryStore.tripTotalAddonsPrice"
            :should-show-share-button="true"
          />
        </div>
      </template>
      <template v-else>
        <TripSummaryColumnMobileLoader />
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
.trip-summary-column {
  background-color: var(--color-white);
  height: 100vh;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .footer {
    padding: var(--spacing-12) var(--spacing-24);
  }

  @include for-less-or-equal-than-1199-px {
    height: auto;
    box-shadow: var(--shadow-elevation-200);
  }

  @include for-less-or-equal-than-1199-px {
    border: 1px solid var(--color-neutral-30);
  }
}
</style>
